import { Button } from '@components/button';
import { ToastType } from '@components/toast';
import { ButtonProps } from '@mui/material';
import { useCreateQuoteCustomerInvite } from '@services/quotes/quote-invite/useCreateQuoteCustomerInvite';
import { rest } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';

interface GenerateQuoteCustomerAccessLinkButtonProps extends Omit<ButtonProps, 'onClick'> {
  onClickCallback?: (inviteLink: string) => void;
  quoteId: string;
  isOnReviewPage?: boolean;
}

export const GenerateQuoteCustomerAccessLinkButton = ({
  quoteId,
  onClickCallback,
  isOnReviewPage,
  size = 'medium',
  sx,
}: GenerateQuoteCustomerAccessLinkButtonProps) => {
  const [disableGenerateLink, setDisableGenerateLink] = useState(false);
  const { createQuoteCustomerInvite, isPending: isCreatingInvite } = useCreateQuoteCustomerInvite();

  const handleGenerateAndCopyLink = async () => {
    const result = await createQuoteCustomerInvite(quoteId);

    if (!result) {
      setDisableGenerateLink(false);
      return;
    }

    const inviteLink = isOnReviewPage
      ? `${window.location.origin}/quote/${quoteId}/review?inviteToken=${result.token}`
      : `${window.location.origin}/quote/${quoteId}?inviteToken=${result.token}`;

    navigator.clipboard.writeText(inviteLink);

    enqueueSnackbar(`Link copied!`, {
      preventDuplicate: true,
      variant: ToastType.Success,
    });

    // in 1 second, re-enable the button
    setTimeout(() => {
      setDisableGenerateLink(false);
    }, 1000);

    if (onClickCallback) {
      onClickCallback(inviteLink);
    }
  };

  const disabled = disableGenerateLink || isCreatingInvite;

  return (
    <Button
      variant='text'
      disabled={disabled}
      size={size}
      isLoading={isCreatingInvite}
      sx={{
        justifyContent: 'left',
        padding: 0,

        ...sx,
      }}
      onClick={handleGenerateAndCopyLink}
      {...rest}
    >
      Generate and Copy Link to Grant Customer Access
    </Button>
  );
};
